import { useRouter } from 'next/router'
import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Night, Panel, Results } from 'components/Menu/HeaderSearch'
import { inTickCard } from 'views/TokenPages/components/VerifyTicks'
import { tokenHref } from 'views/TokenPages/config'
import { useTokenSuggestions } from 'views/TokenPages/hooks'
import { Icon } from 'views/TokenPages/icons'

/*
 * The home hero's token search (Bez, 2026-10-04: "put search on the main page"): the same BNB Smart Chain search as /tokens and the header (local list
 * first, then the chain, each row marked verified or low liquidity / unverified), as the hero's big field under the two buttons ("Swap now" stays the
 * first thing to tab to). One search field per screen: while this one is on screen the header's field and button step aside (html[data-hero-search]),
 * and "/" focuses this one; scrolled past, the header's search is back.
 */
const Wrap = styled.div`
  position: relative;
  z-index: 20; /* the results paint over the husky card fan (its seats go up to z 6 and come later in the page; at 390 the fan sits right under the field) */
  width: 100%;
  max-width: 560px;
  margin-top: 20px;
`
const Box = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  > svg {
    position: absolute;
    left: 16px;
    color: #9fb0d4;
    pointer-events: none;
  }
`
const Field = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 46px 0 46px;
  border-radius: 16px;
  font-family: inherit;
  font-size: 16px; /* 16 px: iOS does not zoom into the field */
  color: #f4f6ff;
  background: rgba(13, 18, 44, 0.86);
  border: 1px solid rgba(127, 212, 232, 0.36);
  box-shadow: 0 0 0 4px rgba(127, 212, 232, 0.06), 0 12px 32px rgba(3, 5, 20, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  caret-color: #7fd4e8;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &::placeholder { color: #9fb0d4; opacity: 1; }
  &:focus {
    outline: none;
    border-color: #7fd4e8;
    box-shadow: 0 0 0 4px rgba(127, 212, 232, 0.22), 0 0 28px rgba(127, 212, 232, 0.25), 0 12px 32px rgba(3, 5, 20, 0.55);
  }
  &::-webkit-search-cancel-button { display: none; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`
const Kbd = styled.kbd`
  position: absolute;
  right: 14px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  color: #9fb0d4;
  border: 1px solid rgba(180, 200, 255, 0.3);
  pointer-events: none;
`
const Clear = styled.button`
  position: absolute;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #9fb0d4;
  cursor: pointer;
  &:hover { color: #f4f6ff; }
  &:focus-visible { outline: 2px solid #7fd4e8; }
`
const Drop = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  white-space: normal;
`
const Hint = styled.p`
  margin: 8px 2px 0;
  font-size: 13px;
  line-height: 18px;
  color: #9fb0d4;
`

export default function HeroSearch() {
  const router = useRouter()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const s = useTokenSuggestions(q)
  const wrap = useRef<HTMLDivElement>(null)
  const field = useRef<HTMLInputElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  // on screen: the header's search steps aside and "/" comes here (56 px = the header over the top of the page)
  useEffect(() => {
    const el = wrap.current
    const root = document.documentElement
    if (!el || typeof IntersectionObserver === 'undefined') return undefined
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) root.dataset.heroSearch = 'on'
      else delete root.dataset.heroSearch
    }, { rootMargin: '-56px 0px 0px 0px' })
    io.observe(el)
    const key = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || root.dataset.heroSearch !== 'on') return
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return
      e.preventDefault()
      field.current?.focus()
    }
    window.addEventListener('keydown', key)
    return () => {
      io.disconnect()
      window.removeEventListener('keydown', key)
      delete root.dataset.heroSearch
    }
  }, [])
  // a click elsewhere closes the results; a picked token (any navigation) clears the field
  useEffect(() => {
    if (!open) return undefined
    const down = (e: PointerEvent) => {
      // a row's tick card is portalled to <body>: a tap inside it is not "elsewhere"
      if (!wrap.current?.contains(e.target as Node) && !inTickCard(e.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', down)
    return () => document.removeEventListener('pointerdown', down)
  }, [open])
  useEffect(() => {
    const done = () => {
      setOpen(false)
      setQ('')
    }
    router.events.on('routeChangeStart', done)
    return () => router.events.off('routeChangeStart', done)
  }, [router.events])

  const links = () => Array.from(panel.current?.querySelectorAll<HTMLAnchorElement>('a[href]') ?? [])
  const fieldKeys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (s.isAddress) router.push(tokenHref(s.address))
      else if (s.rows[0]) router.push(tokenHref(s.rows[0].address))
    } else if (e.key === 'ArrowDown') {
      const first = links()[0]
      if (first) {
        e.preventDefault()
        first.focus()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      if (q) setQ('')
      else field.current?.blur()
      setOpen(false)
    }
  }
  const panelKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const all = links()
    const i = all.indexOf(document.activeElement as HTMLAnchorElement)
    if (e.key === 'ArrowDown' && i >= 0) {
      e.preventDefault()
      all[Math.min(i + 1, all.length - 1)]?.focus()
    } else if (e.key === 'ArrowUp' && i >= 0) {
      e.preventDefault()
      if (i === 0) field.current?.focus()
      else all[i - 1]?.focus()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      field.current?.focus()
    }
  }
  const show = open && Boolean(q.trim())

  return (
    <Wrap ref={wrap} data-testid="home-search">
      <Box>
        <Icon name="search" size={20} />
        <Field
          ref={field}
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={fieldKeys}
          placeholder="Search any BNB Chain token"
          aria-label="Search tokens by name, symbol or address"
          aria-expanded={show}
          aria-controls={show ? 'home-search-panel' : undefined}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          data-testid="home-search-field"
        />
        {q ? (
          <Clear type="button" aria-label="Clear the search" onClick={() => { setQ(''); field.current?.focus() }}><Icon name="close" size={18} /></Clear>
        ) : (
          <Kbd aria-hidden="true">/</Kbd>
        )}
      </Box>
      {/* always there, under the results when they are open: the hero never changes height while you type */}
      <Hint>Name, symbol or 0x address. Price, pools and competitions for each one.</Hint>
      {show && (
        <Drop>
          <Night>
            <Panel id="home-search-panel" ref={panel} data-testid="home-search-panel" onKeyDown={panelKeys}>
              <Results q={q} s={s} />
            </Panel>
          </Night>
        </Drop>
      )}
    </Wrap>
  )
}
